/**
 * Scrollable question list for one strategy stage — one QuestionCard at a time.
 *
 * Belongs here: DirectionalSlideSwap + QuestionCard wiring for the active question.
 * Does not belong here: stage Rives, stage-complete timing, or header progress.
 */
import { ExpertReviewScreen } from '../../analytics/expertReviewEvents'
import { QuestionCard } from '../../shared/components/QuestionCard'
import type { Question } from '../../shared/types/questionnaire.types'
import type { StrategyQuestionnaireAnswersMap } from '../../shared/types/questionnaire.types'
import { DirectionalSlideSwap, SlideDirection } from '../../shared/transitions/DirectionalSlideSwap'

const CONTENT_SHELL_CLASS =
  'bg-white flex flex-col max-w-2xl mx-auto px-5 pt-6 overflow-hidden'

export interface QuestionsContentProps {
  stageId: string | undefined
  currentQuestion: Question | undefined
  visibleQuestions: Question[]
  draftAnswers: StrategyQuestionnaireAnswersMap
  slideDirection: SlideDirection
  contentBottomPadding: number
  onAdvance: (questionId: string, value: string | number) => void
  /** UI selection only — does not advance. */
  updateDraftAnswer: (questionId: string, value: string | number) => void
  onAmountClear: (questionId: string) => void
}

/** Renders the current question with slide transition; empty state when cursor is past last visible Q. */
export function QuestionsContent({
  stageId,
  currentQuestion,
  slideDirection,
  contentBottomPadding,
  draftAnswers,
  onAdvance,
  updateDraftAnswer,
  onAmountClear
}: QuestionsContentProps) {
  if (!currentQuestion || !stageId) {
    return (
      <div style={{ paddingBottom: `${contentBottomPadding}px` }} className={CONTENT_SHELL_CLASS}>
        <p className='text-gray-600'>No more questions. You can go back or continue.</p>
      </div>
    )
  }

  return (
    <div
      className={`${CONTENT_SHELL_CLASS} flex-1`}
      style={{ paddingBottom: `${contentBottomPadding}px` }}
    >
      <DirectionalSlideSwap contentKey={currentQuestion.id} direction={slideDirection}>
        <QuestionCard
          question={currentQuestion}
          answers={draftAnswers}
          onAdvance={onAdvance}
          updateDraftAnswer={updateDraftAnswer}
          onAmountClear={() => onAmountClear(currentQuestion.id)}
          contextId={stageId}
          analyticsScreen={ExpertReviewScreen.StrategyQuestionnaire}
        />
      </DirectionalSlideSwap>
    </div>
  )
}
